import axios from 'axios';
import { LOCAL_COUNTRY } from '@/constants';
import LanguageCode from '@/locale/language-code';
import { AdsGroupParame, AdsGroupReturnData, InstructionReturnData } from './interface';
import { makeJwtFromJSON } from './tokens/token-utils';
import { PhoneNumber } from '@/shared/model/travelpurpose/phone-number.model';
import { OTPChannel } from '@/visa-application/OTPVerification/OTPVerification.component';

const baseApiUrl = 'services/customerservice/api/tls';

export default class SchengenService {
  public async generateFormSchema(client: string, params: any): Promise<any> {
    const exp = await this.getJwtExpDate();
    const jwt = await makeJwtFromJSON(params, { exp });
    return axios.post(baseApiUrl + '/forms/generate-form-schema/' + client, { token: jwt }).then(res => res.data);
  }

  public getUserForms(issuer: string, fg_id?: number): Promise<any> {
    const formGroupUrl = fg_id ? '?fg_id=' + fg_id : '';
    return axios.get(baseApiUrl + '/forms/list/' + LOCAL_COUNTRY + '/' + issuer + formGroupUrl).then(res => res.data);
  }

  public deleteForm(client: string, issuer: string, formId: number): Promise<any> {
    return axios.delete(baseApiUrl + '/forms/form/' + client + '/' + issuer + '/' + formId);
  }

  public getForm(client: string, issuer: string, formId: number): Promise<any> {
    return axios.get(baseApiUrl + '/forms/form/' + client + '/' + issuer + '/' + formId).then(res => res.data);
  }

  public async getFormActions(client: string, issuer: string, formId: number, includeDeleted?: boolean): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      await axios
        .get(baseApiUrl + '/forms/actions/' + client + '/' + issuer + '/' + formId, {
          params: {
            includeDeleted
          }
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }
  public async getAppointmentTimestamp(client: string, issuer: string, formId: number, includeDeleted?: boolean): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      await axios
        .get(baseApiUrl + '/forms/appointmentTimestamp/' + client + '/' + issuer + '/' + formId, {
          params: {
            includeDeleted
          }
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public createMultiActions(client: string, actionInfo: string): Promise<boolean> {
    return axios
      .post(baseApiUrl + '/forms/action/' + client, null, {
        params: {
          actionInfo
        }
      })
      .then(res => res.data)
      .catch(err => {
        throw new Error(err.response);
      });
  }

  public getUserInfo(client: string, issuer: string): Promise<any> {
    return axios
      .get(baseApiUrl + '/account', {
        params: {
          client,
          issuer
        }
      })
      .then(res => res.data);
  }

  public getMultiFormsStageStatus(client: string, issuer: string, fIds?: string): Promise<any> {
    return axios
      .get(baseApiUrl + '/forms/statuses/' + client + '/' + issuer, {
        params: {
          fIds
        }
      })
      .then(res => res.data);
  }

  public async updateForm(client: string, issuer: string, fId: number, body: any, accountType: string) {
    const exp = await this.getJwtExpDate();
    const jwt = await makeJwtFromJSON(body, { exp });
    return axios
      .put(
        baseApiUrl + '/forms/form/' + client + '/' + issuer + '/' + fId,
        { token: jwt },
        {
          params: {
            accountType
          }
        }
      )
      .then(res => res.data);
  }

  public updatePassBack(client: string, issuer: string, fId: number, fi_passback_service: string, fi_passback_address: string) {
    return axios
      .put(baseApiUrl + '/forms/updatePassBack/' + client + '/' + issuer + '/' + fId, null, {
        params: {
          fi_passback_service,
          fi_passback_address
        }
      })
      .then(res => res.data);
  }

  public validateForm(client: string, issuer: string, accountType: string, form: any): Promise<boolean> {
    return axios
      .post<boolean>(baseApiUrl + '/forms/validate/' + client + '/' + issuer, form, {
        params: {
          accountType
        }
      })
      .then(res => res.data)
      .catch(err => {
        throw new Error(err.response);
      });
  }

  public async getFormGroup(client: string, issuer: string): Promise<any> {
    return axios
      .get(baseApiUrl + '/formgroup', {
        params: {
          client,
          issuer
        }
      })
      .then(res => res.data);
  }

  public async getFormGroupById(client: string, issuer: string, fgId: number): Promise<any> {
    return axios.get(baseApiUrl + '/formgroup/' + client + '/' + issuer + '/' + fgId).then(res => res.data);
  }

  public async createFormGroup(client: string, issuer: string, fgName: string): Promise<any> {
    return axios.post(baseApiUrl + '/formgroup?client=' + client + '&issuer=' + issuer + '&fgName=' + fgName).then(res => res.data);
  }

  public async createAdsFormGroup(data: AdsGroupParame): Promise<AdsGroupReturnData> {
    return axios
      .post(
        `${baseApiUrl}/formgroup/${data.client}/${data.issuer}?leaderName=${data.leaderName}&leaderPax=${data.leaderPax}&leaderPhone=${
          data.leaderPhone
        }&fgName=${data.fgName}&fgDepartureDate=${data.fgDepartureDate}&fgReturnArrivalTime=${data.fgReturnArrivalTime}&fgReturnFlight=${
          data.fgReturnFlight
        }`
      )
      .then(res => res.data);
  }

  public async updateFormGroup(client: string, fgId: number, body: any): Promise<any> {
    return axios.put(baseApiUrl + '/formgroup/' + client + '/' + fgId, body).then(res => res.data);
  }

  public async newUpdateFormGroup(client: string, issuer: string, fgId: number, fgApplicationPath: any): Promise<any> {
    return axios.put(baseApiUrl + `/formgroup/${client}/${issuer}/${fgId}?fgApplicationPath=${fgApplicationPath}`).then(res => res.data);
  }

  public async submitFormGroup(client: string, issuer: string, fgId: number): Promise<any> {
    return axios
      .post(`${baseApiUrl}/formgroup/submit`, null, {
        params: {
          client,
          issuer,
          formGroupId: fgId
        }
      })
      .then(res => res.data);
  }

  public createAccountAndCreateFormGroupIfNecessary(client: string, issuer: string): Promise<any> {
    return axios.post(baseApiUrl + '/formgroup/init/' + client + '/' + issuer).then(res => res.data);
  }

  public createFormWithFormGroup(client: string, issuer: string, fgId: number): Promise<any> {
    return axios.post(baseApiUrl + '/forms/form?client=' + client + '&issuer=' + issuer + '&fgId=' + fgId).then(res => res.data);
  }

  public deleteFormGroup(client: string, issuer: string, formGroupId: number): Promise<any> {
    return axios.delete(baseApiUrl + '/formgroup/' + client + '/' + issuer + '/' + formGroupId).then(res => res.data);
  }

  public downloadPdf(client: string, issuer: string, u_id: number, formId: number, language: string): Promise<any> {
    return axios
      .get(baseApiUrl + '/forms/form/' + client + '/' + issuer + '/' + u_id + '/' + formId.toString() + '/' + language, {
        responseType: 'blob'
      })
      .then(res => res.data);
  }

  public downloadDocument(url: string): Promise<any> {
    return axios
      .get(url, {
        responseType: 'blob'
      })
      .then(res => res.data);
  }

  /**
   * Tell the browser to transform xhr response into file.
   * @param data file blob content
   * @param fileName name of the file
   */
  public forceFileDownload(data, fileName) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  public getTrackMyApplicationFormData(client: string, formId: number): Promise<any> {
    return axios.get(baseApiUrl + '/forms/tma/' + client + '/' + formId).then(res => res.data);
  }

  public getFormGroupStagesStatuses(client: string, issuer: string, formGroupId: number): Promise<any> {
    return axios.get(baseApiUrl + '/formgroup/statuses/' + client + '/' + issuer + '/' + formGroupId).then(res => res.data);
  }

  public getFormGroupBasket(issuer: string, formGroupId: number): Promise<any> {
    return axios.get(`${baseApiUrl}/formgroup/getBasket/${LOCAL_COUNTRY}/${issuer}/${formGroupId}`).then(res => res.data);
  }

  public deleteFormGroupBasket(issuer: string, formGroupId: number, skus: string): Promise<any> {
    return axios.delete(`${baseApiUrl}/formgroup/deleteBasket/${LOCAL_COUNTRY}/${issuer}/${formGroupId}?avs=${skus}`).then(res => res.data);
  }

  public deleteFormBasket(issuer: string, formId: number, skus: string): Promise<any> {
    return axios.delete(`${baseApiUrl}/forms/deleteBasket/${LOCAL_COUNTRY}/${issuer}/${formId}?avs=${skus}`).then(res => res.data);
  }

  public addAvsToBasket(issuer: string, formGroupId: number, skusByForm: any): Promise<any> {
    return axios
      .post(baseApiUrl + '/formgroup/addAvsToBasket/' + LOCAL_COUNTRY + '/' + issuer + '/' + formGroupId, skusByForm)
      .then(res => res.data);
  }

  public sendOrderConfirmation(issuer: string, formGroupId: number, language: LanguageCode, skusByForm: any): Promise<any> {
    return axios
      .post(
        baseApiUrl + '/formgroup/sendOrderConfirmation/' + LOCAL_COUNTRY + '/' + issuer + '/' + formGroupId + '/' + language,
        skusByForm
      )
      .then(res => res.data);
  }

  public getFormGroupFees(issuer: string, formGroupId: number): Promise<any> {
    return axios.get(`${baseApiUrl}/formgroup/fees/${LOCAL_COUNTRY}/${issuer}/${formGroupId}`).then(res => res.data);
  }

  /*check amend button is show*/
  public checkGroupAmendAppointmentButton(issuer: string, formGroupId: number, isSchengenCsl: boolean): Promise<any> {
    return axios
      .get(`${baseApiUrl}/forms/checkGroupAmend/${LOCAL_COUNTRY}/${issuer}/${formGroupId}?isCSL=${isSchengenCsl}`)
      .then(res => res.data);
  }

  public consentSubscribe(issuer: string, selectTypes: string): Promise<any> {
    return axios
      .post(baseApiUrl + '/consent/' + LOCAL_COUNTRY + '/' + issuer + '/subscribe?selectTypes=' + selectTypes)
      .then(res => res.data);
  }

  public createAccount(issuer: string, email: string): Promise<any> {
    return axios.post(baseApiUrl + `/create-account?client=${LOCAL_COUNTRY}&issuer=${issuer}&email=${email}`).then(res => res.data);
  }

  public cancelTransaction(formGroupId: number): Promise<any> {
    return axios.get(baseApiUrl + `/transaction_expire/${formGroupId}`).then(res => res.data);
  }

  public async formReorder(client: string, issuer: string, data: { f_id: string; f_order: string }[]): Promise<any> {
    return axios.put(baseApiUrl + `/forms/form/batchUpdateOrder/${client}/${issuer}`, data).then(res => res.data);
  }

  public getInstructionList(client: string, issuer: string, fg_Id: string): Promise<any> {
    return axios.get(baseApiUrl + `/instruction/${client}/${issuer}?fg_Id=${fg_Id}&status=pending`).then(res => res.data);
  }

  public instructionUploadFile(client: string, issuer: string, f_Id: string, t_id: string, files: File[]): Promise<any> {
    const param = new FormData();
    files.map(item => {
      param.append('files', item);
    });
    const headers = { 'Content-Type': 'multipart/form-data' };
    return axios
      .post(baseApiUrl + `/instruction/${client}/${issuer}?status=pending&f_Id=${f_Id}&t_id=${t_id}`, param, headers as any)
      .then(res => res.data);
  }

  public instructionDeleteFile(issuer: string, fId: string, tId: string, fileName: string): Promise<any> {
    return axios
      .delete(baseApiUrl + `/instruction/${issuer}?status=pending&f_Id=${fId}&t_id=${tId}&fileName=${fileName}`)
      .then(res => res.data);
  }
  public instructionConfirm(client: string, issuer: string, tId: string): Promise<any> {
    return axios.put(baseApiUrl + `/instruction/${client}/${issuer}/confirm?t_id=${tId}`).then(res => res.data);
  }
  // Validate mobile number
  public async getVerifiedNumber(client: string, issuer: string, mobileNumber: string): Promise<PhoneNumber> {
    try {
      return await axios.get(baseApiUrl + `/otp/${client}/${issuer}/verify-number?phoneNumber=${mobileNumber}`).then(res => res.data);
    } catch (error) {
      return error.response.data;
    }
  }
  // 波兰手机号验证页面 获取验证的手机号
  // Polish mobile phone number verification page to get the verified mobile phone number
  public getOtpNumberInfo(client: string, issuer: string, fg_id: string): Promise<any> {
    return axios.get(baseApiUrl + `/otp/${client}/${issuer}/${fg_id}`).then(res => res.data);
  }
  // 波兰手机号验证页面 发送验证码
  // Polish mobile phone number verification page send verification code
  public sendOtpCode(client: string, issuer: string, fg_id: string, channel: OTPChannel): Promise<any> {
    return axios.get(baseApiUrl + `/otp/${client}/${issuer}/${fg_id}/code?channel=${channel}`).then(res => res.data);
  }
  // 波兰手机号验证页面 校验验证码
  // Polish mobile phone number verification page verification code
  public verifyOtpCode(client: string, issuer: string, fg_id: string, code: string): Promise<any> {
    return axios.post(baseApiUrl + `/otp/${client}/${issuer}/${fg_id}/code?code=${code}`).then(res => res.data);
  }

  public getJwtExpDate(): Promise<number> {
    return axios.get(baseApiUrl + '/util/jwt-expiration-date').then(res => res.data);
  }
}
